import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2, FolderClosed, ArrowRight, ShieldCheck } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Inicio() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const agreementPending = user?.agreement_accepted === false;

  useEffect(() => {
    if (!agreementPending) api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
  }, [agreementPending]);

  const counts = {
    total: cases.length,
    analisis: cases.filter((c) => c.status === "en_analisis").length,
    terminado: cases.filter((c) => c.status === "terminado").length,
  };

  return (
    <AppLayout variant="firm">
      <div className="ddp-fade">
        <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600, letterSpacing: ".04em" }}>Bienvenido</div>
        <h1 className="ddp-serif" style={{ fontSize: "clamp(30px,4vw,42px)", color: "var(--burgundy-dark)", margin: "6px 0 0", letterSpacing: "-.02em" }}>
          {user?.name}
        </h1>
        <p style={{ color: "var(--muted)", fontSize: 16, marginTop: 10, maxWidth: 560, lineHeight: 1.6 }}>
          Plantee el trabajo que necesita. Revisaremos el alcance antes de solicitar documentación.
        </p>
      </div>

      {agreementPending && (
        <div data-testid="agreement-pending-banner" className="ddp-card ddp-fade" style={{ marginTop: 26, padding: "20px 22px", border: "1px solid #D8B9BE", background: "#F7EDEE", display: "flex", gap: 15, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ background: "var(--burgundy)", color: "#fff", borderRadius: 8, padding: 10 }}><ShieldCheck size={20} /></div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="ddp-serif" style={{ fontSize: 19, color: "var(--burgundy-dark)" }}>Formalización pendiente</div>
            <div style={{ fontSize: 13.5, color: "#5A4548", marginTop: 5, lineHeight: 1.55 }}>Antes de crear un asunto o remitir documentación, debe formalizar el Acuerdo de Encargo de Tratamiento de Datos y Confidencialidad.</div>
          </div>
          <Link to="/acuerdo" data-testid="agreement-pending-cta" className="ddp-btn ddp-btn-primary ddp-btn-sm" style={{ textDecoration: "none" }}>Revisar y aceptar el acuerdo</Link>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 16, marginTop: 32 }}>
        {[
          { label: "Asuntos totales", value: counts.total },
          { label: "En análisis", value: counts.analisis },
          { label: "Terminados", value: counts.terminado },
        ].map((s) => (
          <div key={s.label} className="ddp-card ddp-fade" style={{ padding: "22px 22px" }}>
            <div className="ddp-serif" style={{ fontSize: 40, color: "var(--burgundy)", lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 8, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginTop: 16 }}>
        <Link to={agreementPending ? "/acuerdo" : "/nuevo-analisis"} data-testid="quick-nuevo" className="ddp-card" style={{ padding: "22px", textDecoration: "none", display: "flex", alignItems: "center", gap: 16, color: "var(--ink)" }}>
          <div style={{ background: "var(--burgundy)", color: "#fff", borderRadius: 8, padding: 12 }}><FilePlus2 size={20} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Plantear un trabajo</div>
            <div style={{ fontSize: 13.5, color: "var(--muted)" }}>Explique de forma guiada qué necesita estudiar o preparar.</div>
          </div>
          <ArrowRight size={18} style={{ color: "var(--burgundy)" }} />
        </Link>
        <Link to={agreementPending ? "/acuerdo" : "/asuntos"} data-testid="quick-asuntos" className="ddp-card" style={{ padding: "22px", textDecoration: "none", display: "flex", alignItems: "center", gap: 16, color: "var(--ink)" }}>
          <div style={{ background: "var(--surface)", color: "var(--burgundy)", borderRadius: 8, padding: 12 }}><FolderClosed size={20} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Solicitudes y encargos</div>
            <div style={{ fontSize: 13.5, color: "var(--muted)" }}>Revise propuestas y consulte sus expedientes.</div>
          </div>
          <ArrowRight size={18} style={{ color: "var(--burgundy)" }} />
        </Link>
      </div>

      {cases.length > 0 && (
        <div style={{ marginTop: 34 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 12 }}>Actividad reciente</div>
          <div className="ddp-card">
            {cases.slice(0, 4).map((c, i) => (
              <Link key={c.id} to={`/asuntos/${c.id}`} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", textDecoration: "none", color: "var(--ink)", borderTop: i ? "1px solid var(--line)" : "none" }}>
                <span className="ddp-ref" style={{ minWidth: 110 }}>{c.reference}</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14 }}>{c.title}</span>
                <StatusPill status={c.status_display} />
              </Link>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
